// fork from https://github.com/xmppjs/xmpp.js/blob/8ad208e53a6f3294add74a530ad3c2fa6022b853/packages/sasl/index.js
// changes: add support for refresh token mechanism. This code in the remote repo is quite generic, but here it supports only oauth tokens

import { decode, encode } from '@xmpp/base64'
import xml, { Element } from '@xmpp/xml'
import SASLError from '@xmpp/sasl/lib/SASLError'
import SASLFactory, { Mechanism } from 'saslmechanisms'
import { Entity } from '@xmpp/middleware'
import * as streamFeatures from '@xmpp/stream-features'
import { exponentialSleep } from '../../helpers/exponentialSleep'

// https://xmpp.org/rfcs/rfc6120.html#sasl

const NS = 'urn:ietf:params:xml:ns:xmpp-sasl'
const AUTH_RETRIES = 2

const getMechanismNames = (features: Element) => {
  return features
    .getChild('mechanisms', NS)
    ?.getChildElements()
    .map(el => el.text())
}

const ignoreConnectionClosing = (error: unknown) => {
  if (error instanceof Error && error.message === 'Connection is closing') {
    /* Ignore if connection is closing, there will be another error about it, either failed auth or no internet */
    return
  }
  throw error
}

const refreshToken = (entity: Entity, mechanism: string, refreshToken: string) =>
  new Promise<string>((resolve, reject) => {
    const resolveRequest = (element: Element) => {
      if (element.attrs.xmlns !== NS) {
        return
      }

      if (element.name === 'failure') {
        reject(SASLError.fromElement(element))
      } else if (element.name === 'success') {
        const token = decode(element.getText())
        resolve(token)
      }
      entity.removeListener('nonza', resolveRequest)
    }
    entity.addListener('nonza', resolveRequest)
    entity
      .send(xml('auth', { xmlns: NS, mechanism }, encode(refreshToken)))
      .catch(ignoreConnectionClosing)
  })

const authenticate = async (
  entity: Entity,
  mech: Mechanism,
  credentials: Partial<CredentialsObj>
) => {
  const { domain } = entity.options
  const creds = {
    username: null,
    password: null,
    server: domain,
    host: domain,
    realm: domain,
    serviceType: 'xmpp',
    serviceName: domain,
    ...credentials,
  }

  return new Promise<void>((resolve, reject) => {
    const handler = (element: Element) => {
      if (element.attrs.xmlns !== NS) {
        return
      }

      if (element.name === 'challenge') {
        mech.challenge(decode(element.text()))
        const resp = mech.response(creds)
        entity
          .send(
            xml(
              'response',
              { xmlns: NS, mechanism: mech.name },
              typeof resp === 'string' ? encode(resp) : ''
            )
          )
          .catch(ignoreConnectionClosing)
        return
      }

      entity.removeListener('nonza', handler)

      if (element.name === 'failure') {
        reject(SASLError.fromElement(element))
      } else if (element.name === 'success') {
        resolve()
      }
    }

    entity.on('nonza', handler)

    if ('clientFirst' in mech && mech.clientFirst) {
      entity
        .send(xml('auth', { xmlns: NS, mechanism: mech.name }, encode(mech.response(creds))))
        .catch(ignoreConnectionClosing)
    }
  })
}

type Credentials = Partial<CredentialsObj> | CredentialsFactory

interface CredentialsObj {
  readonly refreshToken?: string
  readonly token: string
}

type CredentialsFactory = (
  callback: (credentials: CredentialsObj) => Promise<void>,
  mechanism: string
) => Promise<void>

const sasl = <TEntity extends Entity>(
  {
    streamFeatures,
  }: {
    streamFeatures: streamFeatures.StreamFeatures<TEntity>
  },
  credentials: Credentials
) => {
  const SASL = new SASLFactory()
  let token = typeof credentials === 'function' ? null : credentials.token

  const authErrorHandler =
    (entity: TEntity, mechName: string, creds: Partial<CredentialsObj>) =>
    async (error: { message: string }) => {
      if (error.message === 'not-authorized' && creds.refreshToken) {
        token = await refreshToken(entity, mechName, creds.refreshToken)
      } else {
        throw error
      }
    }

  streamFeatures.use('mechanisms', NS, async ({ stanza, entity }) => {
    const offered = getMechanismNames(stanza)
    const supported: string[] = (SASL as unknown as { _mechs: Array<{ name: string }> })._mechs.map(
      ({ name }) => name
    )
    const intersection = supported.filter(mech => offered?.includes(mech))
    const possibleMechanism = intersection[0]

    await callWithRetries(async () => {
      if (typeof credentials === 'function') {
        const mech = (
          SASL.create as (mechs: [string | undefined]) => ReturnType<typeof SASL.create>
        )([possibleMechanism])
        if (!mech) {
          throw new Error('No compatible mechanism')
        }
        await credentials(
          creds =>
            authenticate(entity, mech, { ...creds, token: token || creds.token }).catch(
              authErrorHandler(entity, mech.name, { ...creds, token: token || creds.token })
            ),
          mech.name
        )
      } else {
        const mech = (
          SASL.create as (mechs: [string | undefined]) => ReturnType<typeof SASL.create>
        )(credentials.token ? [possibleMechanism] : ['ANONYMOUS'])
        if (!mech) {
          throw new Error('No compatible mechanism')
        }

        await authenticate(entity, mech, {
          ...credentials,
          token: token || credentials.token,
        }).catch(
          authErrorHandler(entity, mech.name, { ...credentials, token: token || credentials.token })
        )
      }
    }, AUTH_RETRIES)

    await entity.restart()
  })

  return {
    use(...args: Parameters<SASLFactory['use']>) {
      return SASL.use(...args)
    },
  }
}

const callWithRetries = async (fn: () => Promise<unknown>, retries: number) => {
  let currentRetry = 0
  while (currentRetry <= retries) {
    try {
      if (currentRetry > 0) {
        // min 500ms, max 1s (at 2nd retry)
        await exponentialSleep(currentRetry, 250)
      }
      return await fn()
    } catch (error) {
      // eslint-disable-next-line max-depth
      if (currentRetry === retries) {
        throw error
      }
    }
    currentRetry += 1
  }
}

export default sasl
